<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import type {
  IOrganizationStatisticsResponse,
  IOrganizationStatistic
} from '~/components/entity/organization/types'
import { LazyVChart } from '~/components/ui/chart'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSeoContent } from '~/components/widget/seo'
import { useFetchLocale } from '~/composables/useFetchLocale'
import { useHumanNumber } from '~/composables/useHumanNumber'

const { t, locale } = useI18n()
const { formatHumanNumber } = useHumanNumber()

interface IBank {
  id: string
  title: string
  logo: string
  color: string
}

interface BankRating {
  bank: string
  logo: string
  value: number
  color: string
}

const dataSeries = [
  { id: 'capital', name: 'common.capital', unit: 'млн' },
  { id: 'revenue', name: 'common.profit', unit: 'млн' },
  { id: 'branches', name: 'common.branches', unit: 'ед.' },
  { id: 'atms', name: 'common.atms', unit: 'ед.' },
  { id: 'terminals', name: 'entity.bank.item.terminals', unit: 'ед.' }
]

const selectedSeries = ref('capital')

const currentDate = new Date()
const currentYear = ref(currentDate.getFullYear())
const currentMonth = ref(String(currentDate.getMonth() + 1).padStart(2, '0'))

const banks = computed<IBank[]>(() => {
  const banksList =
    statisticsData.value?.data?.map((item: IOrganizationStatistic) => ({
      id: item.organization.title.toLowerCase().replace(/\s+/g, '-'),
      title: item.organization.title,
      logo: item.organization.logo,
      color: item.organization.brand_color || '#2563eb'
    })) || []
  return banksList
})

const generateDataForSeries = (seriesId: string) => {
  if (!statisticsData.value?.data?.length) return {}

  const data: Record<string, Record<string, number>> = {}

  statisticsData.value.data.forEach((item: IOrganizationStatistic) => {
    const bankId = item.organization.title.toLowerCase().replace(/\s+/g, '-')
    const key = `${item.year}-${String(item.month).padStart(2, '0')}`

    if (!data[key]) {
      data[key] = {}
    }

    let value = 0
    switch (seriesId) {
      case 'capital':
        value = item.capital
        break
      case 'revenue':
        value = item.revenue
        break
      case 'branches':
        value = item.branches
        break
      case 'atms':
        value = item.atms
        break
      case 'terminals':
        value = item.terminals
        break
    }

    data[key][bankId] = value
  })

  return data
}

const allSeriesData = computed(() => {
  const result: Record<string, Record<string, Record<string, number>>> = {}
  dataSeries.forEach((series) => {
    result[series.id] = generateDataForSeries(series.id)
  })
  return result
})

// show only 2025 and 2026 years
const years = ['2026', '2025']
const selectedYear = ref(String(currentYear.value))

const selectedMonth = ref(currentMonth.value)
const months = [
  { value: '01', label: 'Январь' },
  { value: '02', label: 'Февраль' },
  { value: '03', label: 'Март' },
  { value: '04', label: 'Апрель' },
  { value: '05', label: 'Май' },
  { value: '06', label: 'Июнь' },
  { value: '07', label: 'Июль' },
  { value: '08', label: 'Август' },
  { value: '09', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' }
]

const currentTimeKey = computed(() => `${selectedYear.value}-${selectedMonth.value}`)

const currentData = computed<BankRating[]>(() => {
  const data = allSeriesData.value[selectedSeries.value]?.[currentTimeKey.value] || {}
  const result = banks.value
    .map((bank: IBank) => ({
      bank: bank.title,
      logo: bank.logo,
      value: data[bank.id] || 0,
      color: bank.color
    }))
    .sort((a: BankRating, b: BankRating) => b.value - a.value)

  return result
})

const formatValue = (value: number) => {
  if (selectedSeries.value === 'capital' || selectedSeries.value === 'revenue') {
    return formatHumanNumber(value)
  } else {
    return new Intl.NumberFormat(locale.value === 'ky' ? 'ky-KG' : 'ru-RU').format(value)
  }
}

const option = computed<EChartsOption>(() => {
  const richStyles: Record<
    string,
    {
      height?: number
      width?: number
      align?: string
      backgroundColor?: { image?: string }
      padding?: number[]
      fontSize?: number
      fontWeight?: string | number
      color?: string
    }
  > = {}
  currentData.value.forEach((item: BankRating, index: number) => {
    richStyles[`bank${index}`] = {
      height: 42,
      width: 42,
      align: 'left',
      backgroundColor: {
        image: item.logo
      }
    }
    richStyles[`name${index}`] = {
      padding: [0, 0, 0, 4],
      fontSize: 14,
      fontWeight: '500',
      color: '#2a2d2d',
      align: 'left',
      lineHeight: 42
    }
  })

  const chartHeight = Math.min(100 + (currentData.value?.length || 0) * 50, 800)
  const yAxisHeight = chartHeight - 60

  return {
    grid: {
      top: 10,
      bottom: 20,
      left: 300,
      right: 70,
      height: yAxisHeight
    },
    xAxis: {
      max: 'dataMax',
      axisLabel: {
        formatter: (value: number) => {
          return formatValue(value)
        }
      }
    },
    yAxis: {
      type: 'category',
      data: currentData.value.map((item: BankRating) => item.bank),
      inverse: true,
      animationDuration: 300,
      animationDurationUpdate: 300,
      axisLabel: {
        show: true,
        fontSize: 12,
        fontWeight: '500',
        interval: 0,
        align: 'left',
        margin: 300,
        formatter: (value: string) => {
          const index = currentData.value.findIndex((item: BankRating) => item.bank === value)
          if (index === -1) return value
          const item = currentData.value[index]
          return `{bank${index}|}{name${index}|${item.bank}}`
        },
        rich: richStyles,
        lineHeight: 42
      }
    },
    series: [
      {
        realtimeSort: true,
        type: 'bar',
        data: currentData.value.map((item: BankRating) => item.value),
        label: {
          show: true,
          position: 'right',
          valueAnimation: true,
          formatter: (params: { value: number }) => {
            return formatValue(params.value)
          }
        },
        itemStyle: {
          color: (params: { dataIndex: number }) => {
            return currentData.value[params.dataIndex]?.color || '#2563eb'
          }
        }
      }
    ],
    animationDuration: 0,
    animationDurationUpdate: 1000,
    animationEasing: 'linear',
    animationEasingUpdate: 'linear'
  }
})

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl

const url = () => {
  const queryParams = new URLSearchParams()
  queryParams.append('filter[year]', selectedYear.value)
  queryParams.append('filter[month]', selectedMonth.value)
  return `${baseApiUrl}/organization-statistics?${queryParams.toString()}`
}

const { data: statisticsData, pending: isLoading } =
  useFetchLocale<IOrganizationStatisticsResponse>(url, {
    key: computed(() => `statistics-${selectedYear.value}-${selectedMonth.value}`),
    watch: [selectedYear, selectedMonth],
    immediate: true
  })

const breadcrumbs = [
  { title: t('common.banks'), url: '/banks' },
  { title: t('common.statistics'), url: '/banks/statistics' }
]

const chartHeightPx = computed(() => {
  const h = Math.min(50 + (currentData.value?.length || 0) * 50, 800)
  return { outer: `${h + 40}px`, inner: `${h + 30}px`, chart: `${h + 40}px` }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <div class="space-y-5 xl:col-span-4">
          <WidgetSeoContent position="upper" />

          <ContentBlock variant="white">
            <div class="mb-4 flex flex-wrap items-center justify-end gap-4">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium">{{ t('common.periods.year_1_') }}:</label>
                <Select v-model="selectedYear">
                  <SelectTrigger class="w-[100px]">
                    <SelectValue :placeholder="t('common.periods.year_1_')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="year in years" :key="year" :value="year">
                        {{ year }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div class="flex items-center gap-2">
                <label class="text-sm font-medium">{{ t('common.month') }}:</label>
                <Select v-model="selectedMonth">
                  <SelectTrigger class="w-[130px]">
                    <SelectValue :placeholder="t('common.month')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="month in months" :key="month.value" :value="month.value">
                        {{ month.label }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div
              class="relative mt-4 w-full overflow-x-auto"
              :style="{ height: currentData?.length > 0 ? chartHeightPx.outer : '420px' }"
            >
              <div
                class="min-w-[1200px]"
                :style="{ height: currentData?.length > 0 ? chartHeightPx.inner : '400px' }"
              >
                <LazyVChart
                  v-if="currentData?.length > 0 || isLoading"
                  :option="option"
                  autoresize
                  :style="{ height: currentData?.length > 0 ? chartHeightPx.chart : '400px' }"
                  class="w-full"
                />
                <div v-else class="flex h-[400px] items-center justify-center text-gray-500">
                  {{ $t('common.no_data') }}
                </div>
              </div>
            </div>

            <div class="mt-6 flex justify-center">
              <div class="inline-flex flex-wrap items-center justify-center gap-6">
                <label
                  v-for="series in dataSeries"
                  :key="series.id"
                  class="flex cursor-pointer items-center gap-2 text-sm transition-colors hover:text-blue-600"
                  :class="[selectedSeries === series.id ? 'text-blue-600' : 'text-gray-700']"
                >
                  <span class="relative flex items-center">
                    <span
                      class="h-0.5 w-1.5"
                      :class="selectedSeries === series.id ? 'bg-blue-600' : 'bg-gray-400'"
                    />
                    <input
                      v-model="selectedSeries"
                      type="radio"
                      :value="series.id"
                      class="sr-only"
                    />
                    <span class="relative">
                      <span
                        class="block h-3 w-3 rounded-full border-2"
                        :class="
                          selectedSeries === series.id
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-gray-400 bg-white'
                        "
                      />
                      <span
                        v-if="selectedSeries === series.id"
                        class="absolute inset-0 flex items-center justify-center"
                      >
                        <span class="h-2 w-2 rounded-full bg-white" />
                      </span>
                    </span>
                    <span
                      class="h-0.5 w-1.5"
                      :class="selectedSeries === series.id ? 'bg-blue-600' : 'bg-gray-400'"
                    />
                  </span>
                  {{ $t(series.name) }}
                </label>
              </div>
            </div>
          </ContentBlock>

          <WidgetSeoContent position="bottom" />
        </div>
      </div>
    </Container>
  </Main>
</template>
